import { useState } from "react";
import { genreOptions, yearOptions, ratingOptions, sortOptions } from "@/mocks/filters";
import type { MovieFilterState } from "@/utils/movie";

interface FilterPanelProps {
  value: MovieFilterState;
  onChange: (next: MovieFilterState) => void;
  showSort?: boolean;
}

interface FieldProps {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}

function Field({ label, value, options, onChange }: FieldProps) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-foreground-500">{label}</span>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none rounded-md border border-background-300 bg-background-50 py-2 pl-3 pr-8 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
        >
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        <i
          className="ri-arrow-down-s-line pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-base text-foreground-400"
          aria-hidden="true"
        />
      </div>
    </label>
  );
}

export default function FilterPanel({ value, onChange, showSort = true }: FilterPanelProps) {
  const [open, setOpen] = useState(false);

  const update = (key: keyof MovieFilterState, next: string) => {
    onChange({ ...value, [key]: next });
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <div className="flex items-center justify-between gap-3 p-3 md:hidden">
        <span className="flex items-center gap-2 text-sm font-medium text-foreground-800">
          <i className="ri-filter-3-line text-base text-primary-600" aria-hidden="true" />
          筛选与排序
        </span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors duration-200 hover:border-primary-300 hover:text-primary-600"
        >
          {open ? "收起" : "展开"}
          <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} aria-hidden="true" />
        </button>
      </div>

      <div className={`${open ? "block" : "hidden"} px-3 pb-3 md:block md:p-4`}>
        <div className={`grid grid-cols-2 gap-3 ${showSort ? "md:grid-cols-4" : "md:grid-cols-3"}`}>
          <Field label="类型" value={value.genre} options={genreOptions} onChange={(v) => update("genre", v)} />
          <Field label="年份" value={value.year} options={yearOptions} onChange={(v) => update("year", v)} />
          <Field label="评分" value={value.rating} options={ratingOptions} onChange={(v) => update("rating", v)} />
          {showSort ? (
            <Field label="排序" value={value.sort} options={sortOptions} onChange={(v) => update("sort", v)} />
          ) : null}
        </div>
      </div>
    </div>
  );
}